import { makeAutoObservable, runInAction } from "mobx";

import { getNestedList } from "@/components/suggestions/list/utils";
import {
  searchSuggestionsWithAsync,
  nestSuggestions,
  flattenSuggestions,
  groupSuggestions,
} from "@/components/suggestions/utils";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { createLogger } from "@/utils/logger";
import { ObservedBoolMap } from "@/utils/ObservedBoolMap";

import type {
  SuggestionProviderType,
  SuggestionNodeType,
  ProviderSearchOptions,
  Suggestion,
  SuggestionsGroup,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorModel } from "@/models/EditorModel";
import type { NodeModel } from "@/models/nodes/types";

/**
 * Mode of the suggestion system
 * - `idle`: Default state where suggestions are not shown (e.g., during cursor navigation, when editing is finished)
 * - `active`: Suggestions are visible and interactive (triggered by typing, Ctrl+Space, or when actively editing nodes)
 * - `inline`: Special mode for editing inline-editable nodes where suggestions are available but the dropdown is hidden
 */
export type SuggestionMode = "idle" | "active" | "inline";

/**
 * What the context menu was opened on. `caret` means the box was summoned with
 * no selection, i.e. over whatever is being typed.
 */
export type SuggestionContext =
  | "math_node"
  | "collapsed_block"
  | "text_selection"
  | "empty_space"
  | "caret";

export type SuggestionsOpenSource = "input" | "right_click" | "shortcut";

export const PENDING_SUGGESTION_GROUP_LIMIT = 4;
export const SUGGESTION_GROUP_LIMIT = 30;

const logger = createLogger("SuggestionsModel");

class ActiveItemMap extends ObservedBoolMap<[index: number, listId: null | string]> {
  protected mapKey(index: number, listId: null | string): string {
    return `${listId ?? "root"}-${index}`;
  }
}

export class SuggestionsModel {
  // FIXME: refactor suggestion visibility management
  // https://linear.app/corca-research/issue/D-1071/refactor-suggestion-visibility-management

  private _mode: SuggestionMode = "idle";

  private _currentListGroups: SuggestionsGroup[] = [];

  /** What the providers returned, before the context menu's own search narrowed it. */
  private _unfilteredGroups: SuggestionsGroup[] = [];

  private _activeIndex: number = 0;

  private _activeItemMap = new ActiveItemMap();

  private _nestedListId: string | null = null;

  /** Whether the active suggestion was last changed via keyboard navigation
   *  (vs. pointer hover). Used to scroll the active item into view only for
   *  keyboard navigation, so hovering never fights manual scrolling. */
  private _activeIndexFromKeyboard = false;

  /** Set on pointerdown over the suggestion box, cleared on pointerup.
   *
   * Prevents blur from hiding suggestions while a tap-to-select is in progress.
   */
  private _pointerTouch = false;

  /**
   * Client coordinates of the right-click that opened the popup as a context menu.
   */
  private _pointerAnchor: { x: number; y: number } | null = null;

  /**
   * What the user typed into the context menu's own search input. Separate from
   * `doc.input.value`: it filters the menu instead of driving the providers.
   */
  private _query = "";

  /**
   * What the popup was opened on, when it was opened deliberately. Cleared on
   * every mode transition, so the paths that set `mode` directly (typing, the
   * palettes) leave it undefined rather than inheriting a stale context.
   */
  private _openContext: SuggestionContext | undefined;

  /**
   * The math node that right-click landed on, held apart from the selection so
   * opening the menu leaves the caret and the current selection untouched.
   */
  private _pointerNode: NodeModel | null = null;

  /** Indicates whether async suggestions are currently being resolved */
  private _isLoadingAsync = false;

  /** Abort controller for cancelling pending async suggestion resolution */
  private _asyncAbortController: AbortController | null = null;

  /**
   * Picks the active suggestion the way Enter does. Registered by the text-context
   * suggestions popup so editor keymaps can pick when the Enter keydown never reaches
   * the document (Chrome Android delivers Enter inside the IME composition and
   * ProseMirror re-synthesizes it from the DOM change).
   */
  private _selectActiveHandler: (() => void) | null = null;

  constructor(readonly editor: EditorModel) {
    makeAutoObservable(this);
  }

  /** Returns a disposer that unregisters only if this handler is still the current one. */
  setSelectActiveHandler(handler: () => void) {
    this._selectActiveHandler = handler;

    return () => {
      if (this._selectActiveHandler === handler) {
        this._selectActiveHandler = null;
      }
    };
  }

  /** Returns false when no popup is registered or there is nothing to pick. */
  selectActive(): boolean {
    if (!this._selectActiveHandler || !this.activeSuggestion) {
      return false;
    }

    this._selectActiveHandler();
    return true;
  }

  get activeIndex() {
    return this._activeIndex;
  }

  set activeIndex(value: number) {
    this._activeIndex = value;
    if (value !== -1) {
      this._activeItemMap.set(value, this._nestedListId);
    }

    this.editor.hints.setPhantomHints(this.activeSuggestion?.phantomHints ?? []);
  }

  get activeIndexFromKeyboard() {
    return this._activeIndexFromKeyboard;
  }

  set activeIndexFromKeyboard(value: boolean) {
    this._activeIndexFromKeyboard = value;
  }

  get nestedListId() {
    return this._nestedListId;
  }

  set nestedListId(value: string | null) {
    this._nestedListId = value;
    if (this._activeIndex !== -1) {
      this._activeItemMap.set(this._activeIndex, value);
    }
  }

  get activeSuggestion(): Suggestion | undefined {
    return this.currentList[this._activeIndex];
  }

  set mode(suggestionMode: SuggestionMode) {
    this._mode = suggestionMode;
    this._openContext = undefined;
    if (suggestionMode === "idle") {
      this._pointerTouch = false;
      this._pointerAnchor = null;
      this._pointerNode = null;
    }
  }

  get mode(): SuggestionMode {
    return this._mode;
  }

  get pointerAnchor(): { x: number; y: number } | null {
    return this._pointerAnchor;
  }

  get query(): string {
    return this._query;
  }

  set query(value: string) {
    if (value === this._query) {
      return;
    }

    this._query = value;
    this.resetActiveIndex(0);
  }

  get openContext(): SuggestionContext | undefined {
    return this._openContext;
  }

  get pointerNode(): NodeModel | null {
    return this._pointerNode;
  }

  open(context?: SuggestionContext) {
    this.mode = "active";
    this._openContext = context;
  }

  openAt(x: number, y: number, context?: SuggestionContext, node: NodeModel | null = null) {
    this._pointerAnchor = { x, y };
    this._pointerNode = node;
    this.open(context);
  }

  set pointerTouch(value: boolean) {
    this._pointerTouch = value;
  }

  get pointerTouch(): boolean {
    return this._pointerTouch;
  }

  get isLoadingAsync(): boolean {
    return this._isLoadingAsync;
  }

  get currentListGroups(): SuggestionsGroup[] {
    return this._currentListGroups;
  }

  get unfilteredGroups(): SuggestionsGroup[] {
    return this._unfilteredGroups;
  }

  setFilteredGroups(groups: SuggestionsGroup[]) {
    this._currentListGroups = groups;
  }

  get currentList(): Suggestion[] {
    return getNestedList(this._currentListGroups, this.nestedListId);
  }

  get listSize(): number {
    return this.currentList.length;
  }

  get haveRenderableItems(): boolean {
    return this.currentList.length > 0 && this.currentList.some((s) => !s.hideStandalone);
  }

  setActiveListItem(listId: string | null, index: number) {
    this.nestedListId = listId;
    this.activeIndex = index;
  }

  isActive(index: number, listId: string | null = null): boolean {
    return this._activeItemMap.get(index, listId);
  }

  /**
   * returns true if suggestions can be rendered
   */
  get visible(): boolean {
    return (
      this.available &&
      this.mode !== "idle" &&
      this.mode !== "inline" &&
      this.listSize > 0 &&
      this.haveRenderableItems
    );
  }

  private resetActiveIndex(index = 0) {
    this._activeItemMap.clear();
    this._nestedListId = null;
    this.activeIndex = index;
  }

  reset(idle = true) {
    if (idle) {
      this.mode = "idle";
    }

    this._currentListGroups = [];
    this._unfilteredGroups = [];
    this._query = "";

    this.resetActiveIndex(-1);

    // Cancel any pending async resolution
    if (this._asyncAbortController) {
      this._asyncAbortController.abort();
      this._asyncAbortController = null;
    }
    this._isLoadingAsync = false;

    this.editor.doc.input.setInputPosRange({ from: -1, to: -1 });
  }

  /**
   * returns true if suggestions can be shown in current context
   */
  get available(): boolean {
    if (this.editor.doc.selection.isMathOnlySelection || this.editor.editorState.latexInput) {
      return true;
    }

    if (this.pointerAnchor !== null) {
      return true;
    }

    if (this.editor.doc.input.value.length === 0) {
      return false;
    }

    const { tiptap, editorState } = this.editor;

    const isSlashMode = !editorState.isExpression && this.editor.doc.input.value.startsWith("/");
    if (isSlashMode) {
      return !editorState.isCode;
    }

    const isMentionMode = !editorState.isExpression && this.editor.doc.input.value.startsWith("@");
    if (isMentionMode) {
      return !editorState.isCode;
    }

    if (!editorState.isExpression && tiptap) {
      const { state } = tiptap;

      const from = state.selection.$head.pos;
      const to = state.selection.$anchor.pos;

      if (from !== to) {
        const anchorNodeBefore = state.selection.$anchor.nodeBefore;
        const headNodeBefore = state.selection.$head.nodeBefore;
        const anchorInInlineMark = Boolean(
          anchorNodeBefore?.marks.find(
            (mark) => mark.type.name === "code" || mark.type.name === "link",
          ) ||
            headNodeBefore?.marks.find(
              (mark) => mark.type.name === "code" || mark.type.name === "link",
            ),
        );
        if (!anchorInInlineMark) {
          let nodesCount = 0;
          let shouldShowSuggestions = true;
          state.doc.nodesBetween(Math.min(from, to), Math.max(from, to), (node) => {
            if (
              !["text", "blockquote", "callout", "listItem", "taskItem"].includes(node.type.name)
            ) {
              nodesCount += 1;
            }
            if (
              nodeIsExpression(node) ||
              node.type.name === "documentTitle" ||
              node.type.name === "codeBlock" ||
              node.type.name === "heading" ||
              nodesCount > 2
            ) {
              shouldShowSuggestions = false;
              return false;
            }
            return true;
          });
          return shouldShowSuggestions;
        }
        return false;
      }
    }

    if (
      editorState.isCode ||
      editorState.isLink ||
      editorState.isHeading ||
      editorState.isDocumentTitle
    ) {
      return false;
    }

    return true;
  }

  collectSuggestions<T extends SuggestionNodeType>({
    doc,
    search,
    accept,
    options,
  }: {
    doc: DocumentModel;
    search: string;
    accept: SuggestionProviderType[];
    options?: {
      providerSearchOptions?: ProviderSearchOptions;
      flat?: boolean;
      initialIndex?: number;
    };
  }): SuggestionsGroup[] {
    logger.debug("Collecting suggestions for search:", search);

    // Cancel any pending async resolution
    if (this._asyncAbortController) {
      this._asyncAbortController.abort();
    }
    this._asyncAbortController = new AbortController();
    const { signal } = this._asyncAbortController;

    const { providerSearchOptions, flat, initialIndex } = options ?? {};

    // Get all chunks (sync and async)
    const allChunks = searchSuggestionsWithAsync<T>(
      search,
      doc,
      accept,
      providerSearchOptions,
      signal,
    );

    const syncSuggestions: Suggestion<T>[] = [];
    const asyncChunks: Promise<Suggestion<T>[]>[] = [];

    // Separate sync and async chunks
    for (const chunk of allChunks) {
      if (Array.isArray(chunk)) {
        syncSuggestions.push(...chunk);
      } else {
        asyncChunks.push(chunk);
      }
    }

    // Set initial sync suggestions
    const suggestions = flat
      ? flattenSuggestions(syncSuggestions)
      : nestSuggestions(search, syncSuggestions);
    const sortedGroups = groupSuggestions(search, suggestions, SUGGESTION_GROUP_LIMIT);

    if (sortedGroups.length > 0 || asyncChunks.length === 0) {
      this._unfilteredGroups = sortedGroups;
      this._currentListGroups = sortedGroups;
      this.resetActiveIndex(initialIndex);
    }

    logger.debug("Collected sync suggestions", sortedGroups);

    // Handle async suggestions - process each chunk individually as it resolves
    if (asyncChunks.length > 0) {
      this._isLoadingAsync = true;

      let allResolvedSuggestions = [...syncSuggestions];
      let pendingCount = asyncChunks.length;

      asyncChunks.forEach((chunkPromise) => {
        chunkPromise
          .then((resolvedChunk) => {
            if (signal.aborted) {
              logger.debug("Async suggestion chunk resolution cancelled");
              return;
            }

            logger.debug("Resolved async suggestion chunk", resolvedChunk);

            allResolvedSuggestions = [...allResolvedSuggestions, ...resolvedChunk];

            const nested = flat
              ? flattenSuggestions(allResolvedSuggestions)
              : nestSuggestions(search, allResolvedSuggestions);
            const updatedGroups = groupSuggestions(search, nested, SUGGESTION_GROUP_LIMIT);

            runInAction(() => {
              if (signal.aborted) return;

              this._unfilteredGroups = updatedGroups;
              this._currentListGroups = updatedGroups;

              this.resetActiveIndex(initialIndex);

              logger.debug("Updated suggestions with async chunk", updatedGroups);
            });
          })
          .catch((error) => {
            if (!signal.aborted) {
              logger.debug("Error resolving async suggestion chunk:", error);
            }
          })
          .finally(() => {
            if (signal.aborted) return;
            pendingCount -= 1;
            if (pendingCount === 0) {
              runInAction(() => {
                this._isLoadingAsync = false;
              });
            }
          });
      });
    }

    return sortedGroups;
  }

  private collectionQueue: (() => void)[] = [];

  queueCollection(callback: () => void) {
    this.collectionQueue.push(callback);
  }
}
